/* eslint-disable no-plusplus */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-comment-textnodes */
/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { v4 as uuid4 } from 'uuid'

import { useDispatch } from '../../store/store'
import { showAlertNotification } from '../../store/storeActions'
import { ObservationsResult } from '../../types/observation'
import { get, post, update } from '../../utils/api'
import { PendingRequestsContext } from '../../utils/apiPendingRequests'
import { NON_ANNOTATED_VALUE, UPLOAD_CHUNK_SIZE } from './definitions'
import { UploadImageGroup } from './types'

// Logging prefix for easy filtering in console
const LOG_PREFIX = '[UploadObservation]'

// Interface for flattened image with metadata for chunking
interface ImageWithMeta {
  image: File
  productClass: string
  mask?: File
}

// Helper to flatten imageGroups into a single array of images with metadata
const flattenImageGroups = (
  imageGroups: Array<UploadImageGroup>
): ImageWithMeta[] => {
  const result: ImageWithMeta[] = []

  imageGroups.forEach((imageGroup) => {
    const { images, masks = [], productClass } = imageGroup

    images.forEach((image) => {
      const imageBase = image.name.replace(/\.[^/.]+$/, '')
      const mask = masks.find(
        (m) => m.name.replace(/\.[^/.]+$/, '') === imageBase
      )
      result.push({
        image,
        productClass: productClass || NON_ANNOTATED_VALUE,
        mask,
      })
    })
  })

  return result
}

// Helper to split array into chunks
const splitIntoChunks = <T,>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

// Unique session ID to distinguish logs from different browser tabs
const SESSION_ID = Math.random().toString(36).substring(2, 8)

// Helper to format bytes for logging
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

// Helper to calculate total size of image groups
const calculateImageGroupsStats = (imageGroups: Array<UploadImageGroup>) => {
  let totalImages = 0
  let totalMasks = 0
  let totalImageSize = 0
  let totalMaskSize = 0
  const imageSizes: number[] = []

  imageGroups.forEach((group) => {
    group.images.forEach((img) => {
      totalImages++
      totalImageSize += img.size
      imageSizes.push(img.size)
    })
    group.masks?.forEach((mask) => {
      totalMasks++
      totalMaskSize += mask.size
    })
  })

  return {
    totalImages,
    totalMasks,
    totalImageSize,
    totalMaskSize,
    totalSize: totalImageSize + totalMaskSize,
    formattedImageSize: formatBytes(totalImageSize),
    formattedMaskSize: formatBytes(totalMaskSize),
    formattedTotalSize: formatBytes(totalImageSize + totalMaskSize),
    avgImageSize:
      totalImages > 0 ? formatBytes(totalImageSize / totalImages) : '0',
    largestImage:
      imageSizes.length > 0 ? formatBytes(Math.max(...imageSizes)) : '0',
    smallestImage:
      imageSizes.length > 0 ? formatBytes(Math.min(...imageSizes)) : '0',
  }
}

// Detailed error classifier for network errors
const classifyError = (error: any) => {
  const classification: Record<string, any> = {
    message: error?.message || 'Unknown error',
    code: error?.code,
    name: error?.name,
    timestamp: new Date().toISOString(),
  }

  // Network error classification
  if (error?.message === 'Network Error') {
    classification.type = 'NETWORK_ERROR'
    classification.possibleCauses = [
      'Server closed connection (Too many open files - EMFILE)',
      'Server not responding',
      'Connection refused',
      'Request timeout',
      'CORS issue (unlikely for same-origin)',
    ]
    classification.suggestion =
      'Check backend logs for "Too many open files" or connection errors'
  } else if (error?.code === 'ECONNABORTED') {
    classification.type = 'TIMEOUT'
    classification.possibleCauses = [
      'Request took too long',
      'Network congestion',
    ]
  } else if (error?.response) {
    classification.type = 'SERVER_ERROR'
    classification.status = error.response.status
    classification.statusText = error.response.statusText
    classification.serverMessage = error.response.data
  } else {
    classification.type = 'UNKNOWN'
  }

  return classification
}

// Track pending uploads to detect stuck toasts
const pendingUploads = new Map<
  string,
  {
    startTime: number
    httpCompleteTime?: number
    timeoutId?: ReturnType<typeof setTimeout>
    datasetIds?: string[]
  }
>()

// Time to wait after HTTP complete before warning about stuck toast (30 seconds)
const STUCK_TOAST_WARNING_TIMEOUT = 30000

const checkForStuckToast = (uploadId: string) => {
  const upload = pendingUploads.get(uploadId)
  if (upload && upload.httpCompleteTime) {
    const elapsedSinceHttpComplete = Date.now() - upload.httpCompleteTime
    const totalElapsed = Date.now() - upload.startTime
    console.warn(
      LOG_PREFIX,
      '⚠️⚠️⚠️ STUCK TOAST DETECTED: "Preparing images" still open!',
      {
        sessionId: SESSION_ID,
        uploadId,
        datasetIds: upload.datasetIds,
        elapsedSinceHttpComplete: `${Math.round(
          elapsedSinceHttpComplete / 1000
        )}s`,
        totalElapsed: `${Math.round(totalElapsed / 1000)}s`,
        problem: 'HTTP upload done but WebSocket COMPLETED never processed',
        possibleCauses: [
          '1. WebSocket disconnected (check for TypeError: e is not a function)',
          '2. Backend never sent COMPLETED message',
          '3. ObservationsBrowser not mounted to handle COMPLETED',
          '4. Another tab processed the COMPLETED message',
        ],
        timestamp: new Date().toISOString(),
      }
    )
  }
}

const trackUploadStart = (uploadId: string, datasetIds?: string[]) => {
  pendingUploads.set(uploadId, { startTime: Date.now(), datasetIds })
}

const trackHttpComplete = (uploadId: string) => {
  const upload = pendingUploads.get(uploadId)
  if (upload) {
    upload.httpCompleteTime = Date.now()
    upload.timeoutId = setTimeout(
      () => checkForStuckToast(uploadId),
      STUCK_TOAST_WARNING_TIMEOUT
    )
  }
}

// Called by ObservationsBrowser when upload fully completes
export const markUploadCompleted = (uploadId: string) => {
  const upload = pendingUploads.get(uploadId)
  if (upload) {
    if (upload.timeoutId) clearTimeout(upload.timeoutId)
    console.log(LOG_PREFIX, '✅ Upload fully completed, toast closed', {
      sessionId: SESSION_ID,
      uploadId,
      totalTime: `${Math.round((Date.now() - upload.startTime) / 1000)}s`,
      timestamp: new Date().toISOString(),
    })
    pendingUploads.delete(uploadId)
  }
}

// Helper to poll for observations created by upload and associate with datasets
// This bypasses the WebSocket flow which is unreliable for chunked uploads
const pollAndAssociateWithDatasets = async (
  uploadId: string,
  productId: number,
  datasetIds: string[],
  expectedCount: number,
  dispatch: ReturnType<typeof import('../../store/store').useDispatch>,
  invalidateObservations: () => void,
  maxAttempts = 30,
  pollIntervalMs = 2000
): Promise<void> => {
  console.log(LOG_PREFIX, 'Starting pollAndAssociateWithDatasets', {
    uploadId,
    productId,
    datasetIds,
    expectedCount,
    maxAttempts,
    pollIntervalMs,
    timestamp: new Date().toISOString(),
  })

  // Update toast to show we're waiting for backend processing
  showAlertNotification(dispatch, {
    eventType: 'IMAGE_UPLOAD_UPDATE',
    uploadId,
    status: 'UPDATING_UI',
    progress: 0.5,
  })

  let observationIds: number[] = []
  let attempts = 0

  // Poll until we get all observations or max attempts reached
  while (attempts < maxAttempts) {
    attempts++
    try {
      // Query observations filtered by upload_id to avoid picking up observations
      // from concurrent uploads (race condition fix)
      const limit = expectedCount + 100
      const response = await get<ObservationsResult>(
        `/observations/?product=${productId}&upload_id=${uploadId}&limit=${limit}`
      )

      observationIds = response.results.map((obs) => obs.id)

      console.log(LOG_PREFIX, `Poll attempt ${attempts}/${maxAttempts}`, {
        uploadId,
        foundCount: observationIds.length,
        expectedCount,
        timestamp: new Date().toISOString(),
      })

      // If we have enough observations, proceed
      if (observationIds.length >= expectedCount) {
        console.log(LOG_PREFIX, 'Found enough observations', {
          uploadId,
          foundCount: observationIds.length,
          expectedCount,
          timestamp: new Date().toISOString(),
        })
        break
      }

      // Wait before next poll
      await new Promise<void>((resolve) => {
        setTimeout(resolve, pollIntervalMs)
      })
    } catch (err) {
      console.error(LOG_PREFIX, 'Poll error', {
        uploadId,
        attempt: attempts,
        error: err,
        timestamp: new Date().toISOString(),
      })
      await new Promise<void>((resolve) => {
        setTimeout(resolve, pollIntervalMs)
      })
    }
  }

  if (observationIds.length === 0) {
    console.error(LOG_PREFIX, 'No observations found after polling', {
      uploadId,
      attempts,
      timestamp: new Date().toISOString(),
    })
    // Close toast with error indication
    showAlertNotification(dispatch, {
      eventType: 'IMAGE_UPLOAD_UPDATE',
      uploadId,
      status: 'ERROR',
    })
    toast.error('Failed to find uploaded images')
    return
  }

  // Now associate with datasets
  console.log(LOG_PREFIX, 'Associating observations with datasets', {
    uploadId,
    observationCount: observationIds.length,
    datasetIds,
    timestamp: new Date().toISOString(),
  })

  showAlertNotification(dispatch, {
    eventType: 'IMAGE_UPLOAD_UPDATE',
    uploadId,
    status: 'UPDATING_UI',
    progress: 0.7,
  })

  try {
    for (let i = 0; i < datasetIds.length; i++) {
      const datasetId = datasetIds[i]

      if (datasetId.startsWith('new:')) {
        // Create new dataset
        const newDatasetName = datasetId.substring(4)
        console.log(LOG_PREFIX, `Creating new dataset: "${newDatasetName}"`, {
          uploadId,
          observationCount: observationIds.length,
          timestamp: new Date().toISOString(),
        })

        await post('/dataset/', {
          name: newDatasetName,
          product_id: productId,
          observations_ids: observationIds,
        })

        console.log(
          LOG_PREFIX,
          `Dataset "${newDatasetName}" created successfully`,
          {
            uploadId,
            timestamp: new Date().toISOString(),
          }
        )
        toast.success(`Dataset "${newDatasetName}" created successfully`)
      } else {
        // Add to existing dataset
        console.log(LOG_PREFIX, `Adding to existing dataset: ${datasetId}`, {
          uploadId,
          observationCount: observationIds.length,
          timestamp: new Date().toISOString(),
        })

        // Get current dataset info and observations
        const currentDataset = await get<{ id: number; name: string }>(
          `/dataset/${datasetId}/`
        )
        const existingObs = await get<ObservationsResult>(
          `/observations/?dataset=${datasetId}&limit=10000`
        )
        const existingIds = existingObs.results.map((obs) => obs.id)

        // Merge with new observations
        const mergedIds = [...new Set([...existingIds, ...observationIds])]

        await update(`/dataset/${datasetId}/`, {
          name: currentDataset.name,
          product_id: productId,
          observations_ids: mergedIds,
        })

        console.log(
          LOG_PREFIX,
          `Added ${observationIds.length} images to dataset "${currentDataset.name}"`,
          {
            uploadId,
            previousCount: existingIds.length,
            newTotal: mergedIds.length,
            timestamp: new Date().toISOString(),
          }
        )
        toast.success(`Images added to dataset "${currentDataset.name}"`)
      }
    }

    // Emit event to notify other components
    window.dispatchEvent(new CustomEvent('observations-added-to-dataset'))

    // Close toast successfully
    showAlertNotification(dispatch, {
      eventType: 'IMAGE_UPLOAD_UPDATE',
      uploadId,
      status: 'COMPLETED_UPDATING_UI',
      progress: 1,
    })
    markUploadCompleted(uploadId)
    invalidateObservations()
    window.dispatchEvent(new CustomEvent('observations-changed'))

    console.log(LOG_PREFIX, 'Dataset association completed successfully', {
      uploadId,
      datasetIds,
      observationCount: observationIds.length,
      timestamp: new Date().toISOString(),
    })
  } catch (err) {
    console.error(LOG_PREFIX, 'Dataset association failed', {
      uploadId,
      error: err,
      timestamp: new Date().toISOString(),
    })
    showAlertNotification(dispatch, {
      eventType: 'IMAGE_UPLOAD_UPDATE',
      uploadId,
      status: 'ERROR',
    })
    toast.error('Failed to associate images with dataset')
  }

  // Clean up localStorage
  try {
    const uploadDatasetMap = JSON.parse(
      localStorage.getItem('uploadDatasetMap') || '{}'
    )
    delete uploadDatasetMap[uploadId]
    localStorage.setItem('uploadDatasetMap', JSON.stringify(uploadDatasetMap))

    const chunkTracker = JSON.parse(
      localStorage.getItem('chunkTracker') || '{}'
    )
    delete chunkTracker[uploadId]
    localStorage.setItem('chunkTracker', JSON.stringify(chunkTracker))
  } catch (cleanupErr) {
    console.error(LOG_PREFIX, 'Cleanup error', { cleanupErr })
  }
}

const useCreateObservation = () => {
  const [inProgress, setInProgress] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0)

  const { increasePendingRequest, decreasePendingRequest } = useContext(
    PendingRequestsContext
  )

  const resetProgress = () => {
    setProgress(0)
    setInProgress(false)
  }

  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const invalidateObservations = () =>
    queryClient.invalidateQueries({ queryKey: ['observations'] })

  const createObservation = ({
    imageGroups,
  }: {
    imageGroups: Array<UploadImageGroup>
  }) => {
    const controller = new AbortController()
    resetProgress()

    const datasetIds =
      imageGroups[0]?.datasets ||
      (imageGroups[0]?.dataset ? [imageGroups[0].dataset] : [])

    const stats = calculateImageGroupsStats(imageGroups)
    console.log(LOG_PREFIX, '📤 createObservation called', {
      sessionId: SESSION_ID,
      imageGroupsCount: imageGroups.length,
      datasetIds,
      productId: imageGroups[0]?.productId,
      ...stats,
      timestamp: new Date().toISOString(),
    })

    // If no datasets selected, make API call(s) - chunked if needed
    if (datasetIds.length === 0) {
      const uploadId = uuid4()
      trackUploadStart(uploadId)

      // Flatten all images with metadata for chunking
      const allImages = flattenImageGroups(imageGroups)
      const chunks = splitIntoChunks(allImages, UPLOAD_CHUNK_SIZE)
      const totalChunks = chunks.length
      const needsChunking = totalChunks > 1

      console.log(LOG_PREFIX, 'No datasets selected, preparing upload', {
        sessionId: SESSION_ID,
        uploadId,
        totalImages: allImages.length,
        chunkSize: UPLOAD_CHUNK_SIZE,
        totalChunks,
        needsChunking,
        timestamp: new Date().toISOString(),
      })

      showAlertNotification(dispatch, {
        eventType: 'IMAGE_UPLOAD_UPDATE',
        uploadId,
        status: 'PREPARING_UPLOAD',
        progress: 0.01,
      })

      increasePendingRequest()

      // Helper to upload a single chunk
      const uploadChunk = async (
        chunk: ImageWithMeta[],
        chunkIndex: number
      ): Promise<void> => {
        const chunkUploadId =
          totalChunks === 1 ? uploadId : `${uploadId}_chunk_${chunkIndex}`

        const payload = new FormData()
        payload.append('product', imageGroups[0].productId.toString())
        payload.append('observation_type', 'TRAINING')
        payload.append('upload_id', chunkUploadId)
        // Send parent upload ID so backend can aggregate chunks
        if (needsChunking) {
          payload.append('parent_upload_id', uploadId)
          payload.append('chunk_index', chunkIndex.toString())
          payload.append('total_chunks', totalChunks.toString())
        }

        chunk.forEach((item) => {
          payload.append('product_classes', item.productClass)
          payload.append('images', item.image)
          if (item.mask) {
            payload.append('masks', item.mask)
          }
        })

        // Add mapping from first imageGroup if present (on every chunk so
        // segmentation masks are processed for all chunks, not just the first)
        const mapping = imageGroups[0]?.mapping
        if (mapping) {
          payload.append('mapping', mapping)
        }

        console.log(
          LOG_PREFIX,
          `Uploading chunk ${chunkIndex + 1}/${totalChunks}`,
          {
            uploadId,
            chunkUploadId,
            chunkIndex,
            imagesInChunk: chunk.length,
            timestamp: new Date().toISOString(),
          }
        )

        await post(
          '/observations/bulk-create/',
          payload,
          {
            headers: {
              'content-type': 'multipart/form-data',
            },
            signal: controller.signal,
            onUploadProgress(progressEvent: ProgressEvent) {
              const chunkProgress = progressEvent.loaded / progressEvent.total
              // Calculate overall progress: completed chunks + current chunk progress
              const overallProgress = (chunkIndex + chunkProgress) / totalChunks
              setProgress(overallProgress)

              // Log at key progress milestones
              if (
                chunkProgress === 1 ||
                chunkProgress < 0.01 ||
                Math.floor(chunkProgress * 10) !==
                  Math.floor(
                    ((progressEvent.loaded - 1) / progressEvent.total) * 10
                  )
              ) {
                console.log(LOG_PREFIX, 'Chunk upload progress', {
                  uploadId,
                  chunkIndex,
                  chunkProgress,
                  overallProgress,
                  loaded: progressEvent.loaded,
                  total: progressEvent.total,
                  timestamp: new Date().toISOString(),
                })
              }

              showAlertNotification(dispatch, {
                eventType: 'IMAGE_UPLOAD_UPDATE',
                uploadId,
                status: 'PREPARING_UPLOAD',
                progress: overallProgress,
              })
            },
          },
          true
        )
      }

      // Upload all chunks sequentially
      const uploadAllChunks = async () => {
        try {
          for (let i = 0; i < chunks.length; i++) {
            await uploadChunk(chunks[i], i)
            console.log(LOG_PREFIX, `Chunk ${i + 1}/${totalChunks} completed`, {
              uploadId,
              timestamp: new Date().toISOString(),
            })
          }

          trackHttpComplete(uploadId)
          console.log(LOG_PREFIX, 'All chunks uploaded successfully', {
            sessionId: SESSION_ID,
            uploadId,
            totalChunks,
            timestamp: new Date().toISOString(),
          })

          // When using chunking, close the toast directly since backend
          // won't send COMPLETED for the parent uploadId
          if (needsChunking) {
            console.log(
              LOG_PREFIX,
              'Chunked upload complete - closing toast directly',
              { uploadId }
            )
            showAlertNotification(dispatch, {
              eventType: 'IMAGE_UPLOAD_UPDATE',
              uploadId,
              status: 'COMPLETED_UPDATING_UI',
              progress: 1,
            })
            invalidateObservations()
            window.dispatchEvent(new CustomEvent('observations-changed'))
          }
        } catch (error) {
          const errorInfo = classifyError(error)
          console.error(
            LOG_PREFIX,
            '❌ POST /observations/bulk-create/ FAILED',
            {
              uploadId,
              sessionId: SESSION_ID,
              ...errorInfo,
              uploadStats: stats,
            }
          )
          if (errorInfo.type === 'NETWORK_ERROR') {
            console.error(
              LOG_PREFIX,
              '⚠️ NETWORK ERROR - This often indicates the backend ran out of file descriptors.',
              '\n  - Check backend logs for "Too many open files" or "EMFILE"',
              '\n  - Consider uploading fewer images at once',
              '\n  - Check server ulimit settings'
            )
          }
          showAlertNotification(dispatch, {
            eventType: 'IMAGE_UPLOAD_UPDATE',
            uploadId,
            status: 'ERROR',
          })
        } finally {
          console.log(LOG_PREFIX, 'Upload request completed (finally block)', {
            uploadId,
          })
          resetProgress()
          decreasePendingRequest()
        }
      }

      uploadAllChunks()
      return
    }

    // For multiple datasets, upload images ONCE and save all dataset IDs
    const uploadId = uuid4()
    trackUploadStart(uploadId, datasetIds)

    // Flatten all images with metadata for chunking
    const allImages = flattenImageGroups(imageGroups)
    const chunks = splitIntoChunks(allImages, UPLOAD_CHUNK_SIZE)
    const totalChunks = chunks.length
    const needsChunking = totalChunks > 1

    console.log(LOG_PREFIX, 'Upload with dataset(s) selected', {
      sessionId: SESSION_ID,
      uploadId,
      datasetIds,
      totalImages: allImages.length,
      chunkSize: UPLOAD_CHUNK_SIZE,
      totalChunks,
      needsChunking,
      isNewDataset: datasetIds.some((id: string) => id.startsWith('new:')),
      timestamp: new Date().toISOString(),
    })

    // Save all datasetIds in localStorage for later retrieval
    // Also save for each chunk uploadId so ObservationsBrowser can find the mapping
    const uploadDatasetMap = JSON.parse(
      localStorage.getItem('uploadDatasetMap') || '{}'
    )
    uploadDatasetMap[uploadId] = datasetIds
    // Save mapping for each chunk uploadId as well
    if (needsChunking) {
      for (let i = 0; i < totalChunks; i++) {
        const chunkUploadId = `${uploadId}_chunk_${i}`
        uploadDatasetMap[chunkUploadId] = datasetIds
      }
    }
    localStorage.setItem('uploadDatasetMap', JSON.stringify(uploadDatasetMap))

    // Save chunk tracking info for ObservationsBrowser to track completions
    if (needsChunking) {
      const chunkTracker = JSON.parse(
        localStorage.getItem('chunkTracker') || '{}'
      )
      chunkTracker[uploadId] = {
        totalChunks,
        completedChunks: [],
        datasetIds,
        startTime: Date.now(),
      }
      localStorage.setItem('chunkTracker', JSON.stringify(chunkTracker))
    }

    console.log(LOG_PREFIX, 'Saved datasetIds to localStorage', {
      uploadId,
      datasetIds,
      needsChunking,
      totalChunks,
      uploadDatasetMapKeys: Object.keys(uploadDatasetMap),
    })

    showAlertNotification(dispatch, {
      eventType: 'IMAGE_UPLOAD_UPDATE',
      uploadId,
      status: 'PREPARING_UPLOAD',
      progress: 0.01,
      datasetId: datasetIds[0], // Keep backward compatibility for notification
    })

    increasePendingRequest()

    // Helper to upload a single chunk
    const uploadChunk = async (
      chunk: ImageWithMeta[],
      chunkIndex: number
    ): Promise<void> => {
      const chunkUploadId =
        totalChunks === 1 ? uploadId : `${uploadId}_chunk_${chunkIndex}`

      const payload = new FormData()
      payload.append('product', imageGroups[0].productId.toString())
      payload.append('observation_type', 'TRAINING')
      payload.append('upload_id', chunkUploadId)
      // Send parent upload ID so backend can aggregate chunks
      if (needsChunking) {
        payload.append('parent_upload_id', uploadId)
        payload.append('chunk_index', chunkIndex.toString())
        payload.append('total_chunks', totalChunks.toString())
      }

      chunk.forEach((item) => {
        payload.append('product_classes', item.productClass)
        payload.append('images', item.image)
        if (item.mask) {
          payload.append('masks', item.mask)
        }
      })

      // Add mapping from first imageGroup if present (on every chunk so
      // segmentation masks are processed for all chunks, not just the first)
      const mapping = imageGroups[0]?.mapping
      if (mapping) {
        payload.append('mapping', mapping)
      }

      console.log(
        LOG_PREFIX,
        `Uploading chunk ${chunkIndex + 1}/${totalChunks} (with datasets)`,
        {
          uploadId,
          chunkUploadId,
          datasetIds,
          chunkIndex,
          imagesInChunk: chunk.length,
          timestamp: new Date().toISOString(),
        }
      )

      await post(
        '/observations/bulk-create/',
        payload,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
          signal: controller.signal,
          onUploadProgress(progressEvent: ProgressEvent) {
            const chunkProgress = progressEvent.loaded / progressEvent.total
            // Calculate overall progress: completed chunks + current chunk progress
            const overallProgress = (chunkIndex + chunkProgress) / totalChunks
            setProgress(overallProgress)

            // Log at key progress milestones
            if (
              chunkProgress === 1 ||
              chunkProgress < 0.01 ||
              Math.floor(chunkProgress * 10) !==
                Math.floor(
                  ((progressEvent.loaded - 1) / progressEvent.total) * 10
                )
            ) {
              console.log(LOG_PREFIX, 'Chunk upload progress (with datasets)', {
                uploadId,
                datasetIds,
                chunkIndex,
                chunkProgress,
                overallProgress,
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                timestamp: new Date().toISOString(),
              })
            }

            showAlertNotification(dispatch, {
              eventType: 'IMAGE_UPLOAD_UPDATE',
              uploadId,
              status: 'PREPARING_UPLOAD',
              progress: overallProgress,
            })
          },
        },
        true
      )
    }

    // Upload all chunks sequentially
    const uploadAllChunks = async () => {
      try {
        for (let i = 0; i < chunks.length; i++) {
          await uploadChunk(chunks[i], i)
          console.log(
            LOG_PREFIX,
            `Chunk ${i + 1}/${totalChunks} completed (with datasets)`,
            {
              uploadId,
              datasetIds,
              timestamp: new Date().toISOString(),
            }
          )
        }

        trackHttpComplete(uploadId)
        console.log(
          LOG_PREFIX,
          'All chunks uploaded (with datasets) successfully',
          {
            sessionId: SESSION_ID,
            uploadId,
            datasetIds,
            totalChunks,
            timestamp: new Date().toISOString(),
          }
        )

        // For chunked uploads with datasets, bypass WebSocket and handle directly
        // This is more robust as WebSocket flow can be unreliable for chunked uploads
        if (needsChunking) {
          console.log(
            LOG_PREFIX,
            'Chunked upload complete (with datasets) - handling dataset association directly',
            {
              uploadId,
              datasetIds,
              totalChunks,
              totalImages: allImages.length,
            }
          )

          // Call the direct association function
          pollAndAssociateWithDatasets(
            uploadId,
            imageGroups[0].productId,
            datasetIds,
            allImages.length,
            dispatch,
            invalidateObservations
          )
        }
      } catch (error) {
        const errorInfo = classifyError(error)
        console.error(
          LOG_PREFIX,
          '❌ POST /observations/bulk-create/ FAILED (with datasets)',
          {
            uploadId,
            datasetIds,
            sessionId: SESSION_ID,
            ...errorInfo,
            uploadStats: stats,
          }
        )
        if (errorInfo.type === 'NETWORK_ERROR') {
          console.error(
            LOG_PREFIX,
            '⚠️ NETWORK ERROR - This often indicates the backend ran out of file descriptors.',
            '\n  - Check backend logs for "Too many open files" or "EMFILE"',
            '\n  - Consider uploading fewer images at once',
            '\n  - Check server ulimit settings'
          )
        }
        showAlertNotification(dispatch, {
          eventType: 'IMAGE_UPLOAD_UPDATE',
          uploadId,
          status: 'ERROR',
        })
      } finally {
        console.log(
          LOG_PREFIX,
          'Upload request completed (finally block, with datasets)',
          {
            uploadId,
            datasetIds,
          }
        )
        resetProgress()
        decreasePendingRequest()
      }
    }

    uploadAllChunks()
  }
  return { createObservation, inProgress, progress }
}

export default useCreateObservation
